import { ReactNode } from "react";
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Settings } from "@mui/icons-material";
import MDEditor from "@uiw/react-md-editor/nohighlight";
import rehypeSanitize from "rehype-sanitize";
import { useTranslation } from "react-i18next";
import { GroupIcon } from "./Icons";
import {
  groupBadgeStyle,
  groupSectionBodyStyle,
  groupSectionStyle,
} from "./question.style";

type Props = {
  question: any;
  memberCount: number;
  variant?: "builder" | "preview" | "response" | "mobile";
  header?: ReactNode;
  children?: ReactNode;
  onEdit?: () => void;
};

const QuestionGroupSection = ({
  question,
  memberCount,
  variant = "builder",
  header,
  children,
  onEdit,
}: Props) => {
  const { t } = useTranslation("study-designer");
  const isMobile = variant === "mobile";
  const memberCountLabel =
    memberCount === 1
      ? t("FORM_BUILDER.QUESTION_PROPERTIES.GROUP_MEMBER_COUNT_ONE")
      : t("FORM_BUILDER.QUESTION_PROPERTIES.GROUP_MEMBER_COUNT", {
          count: memberCount,
        });

  return (
    <Box
      sx={{
        ...groupSectionStyle,
        ...(isMobile && {
          mb: 0,
          borderRadius: "12px",
          boxShadow: "none",
          bgcolor: "#EEF2F8",
          minWidth: 0,
          maxWidth: "100%",
          overflow: "visible",
        }),
      }}
    >
        {header ?? (
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{ px: isMobile ? 1.5 : 2, py: isMobile ? 1.25 : 2, gap: 1.5 }}
          >
            <Stack direction="row" alignItems="flex-start" gap={1.5} sx={{ minWidth: 0 }}>
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  width: isMobile ? 36 : 40,
                  height: isMobile ? 36 : 40,
                  borderRadius: 1,
                  bgcolor: "#fff",
                  border: "1px solid",
                  borderColor: "#D7E3F4",
                  flexShrink: 0,
                }}
              >
                <GroupIcon />
              </Stack>
              <Box sx={{ minWidth: 0 }}>
                <Box>
                  <MDEditor.Markdown
                    wrapperElement={{
                      "data-color-mode": "light",
                    }}
                    source={question.labelMarkdown || question.label || ""}
                    rehypePlugins={[rehypeSanitize]}
                    style={{
                      fontWeight: isMobile ? 500 : 400,
                      backgroundColor: "transparent",
                    }}
                  />
                </Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                  mt={isMobile ? 0.5 : 1}
                >
                  {question.varname && (
                    <Typography variant="body2" color="text.secondary">
                      ({question.varname})
                    </Typography>
                  )}
                  <Chip
                    size="small"
                    label={t("FORM_BUILDER.QUESTION_PROPERTIES.GROUP_BADGE")}
                    sx={groupBadgeStyle}
                  />
                  {!isMobile && (
                    <Typography variant="caption" color="text.secondary">
                      {memberCountLabel}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
            {onEdit && (
              <Tooltip title={t("FORM_BUILDER.TOOLTIP_VIEW_DETAILS")}>
                <IconButton onClick={onEdit} size="small">
                  <Settings color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}

        <Box
          sx={{
            ...groupSectionBodyStyle,
            ...(isMobile && { px: 1.5, pb: 1.5, pt: 0, gap: 1.25 }),
            borderTop: "1px solid",
            borderColor: "#D7E3F4",
            pt: 1.5,
          }}
        >
          {children ?? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
              {t("FORM_BUILDER.QUESTION_PROPERTIES.GROUP_EMPTY_MEMBERS")}
            </Typography>
          )}
        </Box>
    </Box>
  );
};

export default QuestionGroupSection;
